import type { FC, PointerEvent, ReactNode } from 'react'
import type { IndustrySummary } from '@/types'

import Link from 'next/link'
import { useRef, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/motion/reveal'
import { ToneSection } from '@/components/generals/tone'
import { industries } from '@/constants/content'

import {
    AnimatePresence,
    motion,
    useMotionValue,
    useReducedMotion,
    useScroll,
    useSpring,
    useTransform
} from 'motion/react'

const previewStyles: Record<string, string> = {
    crypto: 'bg-[#c9b8ff] text-[#140c2b]',
    gaming: 'bg-mint text-[#0a0c0c]',
    'prepaid-cards': 'bg-sky text-[#03111d]'
}

interface RowProps {
    industry: IndustrySummary
    position: number
    dimmed: boolean
    onEnter: (industry: IndustrySummary) => void
}

const Row: FC<RowProps> = ({ industry, position, dimmed, onEnter }): ReactNode => {
    const ref = useRef<HTMLLIElement>(null)
    const reduce = useReducedMotion()
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'center center'] })
    const x = useTransform(scrollYProgress, [0, 1], reduce ? ['0%', '0%'] : [position % 2 === 0 ? '-12%' : '12%', '0%'])

    return (
        <li ref={ref} className='overflow-hidden border-t border-tone-line last:border-b'>
            <Link
                href={`/industry/${industry.slug}`}
                onMouseEnter={() => onEnter(industry)}
                onFocus={() => onEnter(industry)}
                data-cursor='Explore'
                className='group grid grid-cols-12 items-center gap-4 py-6 md:py-8'
            >
                <span className='col-span-12 t-label text-tone-muted md:col-span-1'>
                    {industry.index}
                </span>

                <motion.span
                    style={{ x }}
                    className={cn(
                        'col-span-10 block font-heading text-[clamp(2.75rem,8vw,8.5rem)] font-semibold uppercase leading-[0.86] tracking-[-0.04em] md:col-span-9',
                        'transition-[font-variation-settings,opacity,color] duration-700 ease-out-expo [font-variation-settings:"wdth"_72] group-hover:[font-variation-settings:"wdth"_100] group-hover:text-tone-accent',
                        dimmed && 'opacity-30'
                    )}
                >
                    {industry.name}
                </motion.span>

                <span className='col-span-2 flex justify-end'>
                    <span className='flex size-12 items-center justify-center border border-tone-line transition-colors duration-500 group-hover:border-tone group-hover:bg-tone group-hover:text-tone-bg md:size-16'>
                        <ArrowUpRight className='size-5 transition-transform duration-500 group-hover:rotate-45' aria-hidden />
                    </span>
                </span>
            </Link>
        </li>
    )
}

// Three giant rows. Hovering one floats a preview of that industry beside the
// cursor in its own colour.
export const IndustriesIndex: FC = (): ReactNode => {
    const area = useRef<HTMLDivElement>(null)
    const [hovered, setHovered] = useState<IndustrySummary | null>(null)
    const x = useMotionValue(0)
    const y = useMotionValue(0)
    const springX = useSpring(x, { stiffness: 180, damping: 22 })
    const springY = useSpring(y, { stiffness: 180, damping: 22 })

    const onMove = (event: PointerEvent<HTMLDivElement>) => {
        if (!area.current || event.pointerType !== 'mouse') return

        const rect = area.current.getBoundingClientRect()

        x.set(event.clientX - rect.left)
        y.set(event.clientY - rect.top)
    }

    return (
        <ToneSection tone='void' id='industries' className='px-5 py-28 md:px-8 lg:px-12 lg:py-40'>
            <Reveal className='mb-14 grid gap-6 md:grid-cols-12 lg:mb-20'>
                <span className='t-label text-tone-muted md:col-span-3'>
                    (05) Industries
                </span>
                <h2 className='font-heading t-h2 font-semibold uppercase wdth-75 md:col-span-9'>
                    The payment fraud prevention provider of choice for
                </h2>
            </Reveal>

            <div ref={area} className='relative' onPointerMove={onMove} onMouseLeave={() => setHovered(null)}>
                <ul>
                    {industries.map((industry, position) => (
                        <Row
                            key={industry.slug}
                            industry={industry}
                            position={position}
                            dimmed={hovered !== null && hovered.slug !== industry.slug}
                            onEnter={setHovered}
                        />
                    ))}
                </ul>

                <motion.div
                    aria-hidden
                    className='pointer-events-none absolute left-0 top-0 z-10 hidden lg:block'
                    style={{ x: springX, y: springY }}
                >
                    <AnimatePresence>
                        {hovered && (
                            <motion.div
                                key={hovered.slug}
                                className={cn('ml-10 flex w-72 -translate-y-1/2 flex-col gap-10 p-6', previewStyles[hovered.slug])}
                                initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
                                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                transition={{ type: 'spring', stiffness: 260, damping: 24 }}
                            >
                                <span className='t-label opacity-70'>
                                    {hovered.index} / {hovered.shortName}
                                </span>
                                <span className='font-serif text-3xl italic leading-[1.05]'>
                                    {hovered.tagline}
                                </span>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </ToneSection>
    )
}
